import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import apiClient from '../api';
import { currentScreen, screenFromPath, usageSessionId, viewportClass } from '../utils/usageScreen';

const HEARTBEAT_MS = 60 * 1000;
const SCREEN_WATCH_MS = 5 * 1000;
const MIN_FLUSH_MS = 5 * 1000;

function send(body, keepalive = false) {
    const url = `${apiClient.defaults.baseURL}/audit/usage`;
    if (keepalive && typeof fetch === 'function') {
        const token = localStorage.getItem('authToken');
        fetch(url, {
            method: 'POST',
            credentials: 'include',
            keepalive: true,
            headers: {
                'Content-Type': 'application/json',
                ...(token ? { Authorization: `Bearer ${token}` } : {}),
            },
            body: JSON.stringify(body),
        }).catch(() => {});
        return;
    }
    apiClient.post('/audit/usage', body).catch(() => {});
}

export default function UsageTracker() {
    const location = useLocation();
    const lastTick = useRef(Date.now());
    const pathRef = useRef(location.pathname);
    const creditedPath = useRef(location.pathname);
    const screenRef = useRef(screenFromPath(location.pathname));
    const flushRef = useRef(() => {});

    useEffect(() => {
        const sessionId = usageSessionId();
        let started = false;

        function flush(keepalive) {
            const now = Date.now();
            const durationMs = now - lastTick.current;
            const previous = screenRef.current;
            const path = creditedPath.current;
            const nextPath = pathRef.current;
            const next = currentScreen(nextPath);
            lastTick.current = now;
            screenRef.current = next;
            creditedPath.current = nextPath;
            const viewport = viewportClass();
            if (durationMs >= MIN_FLUSH_MS && previous) {
                send({
                    kind: 'heartbeat',
                    durationMs,
                    path,
                    screen: previous,
                    viewport,
                    sessionId,
                }, keepalive);
            }
            if (started && next && next !== previous) {
                send({
                    kind: 'event',
                    action: 'view',
                    path: nextPath,
                    screen: next,
                    fromScreen: previous,
                    viewport,
                    sessionId,
                });
            }
        }

        flushRef.current = flush;
        pathRef.current = location.pathname;
        const initial = currentScreen(location.pathname);
        screenRef.current = initial;
        lastTick.current = Date.now();
        send({
            kind: 'event',
            action: 'view',
            path: location.pathname,
            screen: initial,
            viewport: viewportClass(),
            sessionId,
        });
        started = true;

        const heartbeat = window.setInterval(() => {
            if (document.visibilityState === 'visible') flush(false);
        }, HEARTBEAT_MS);
        const watch = window.setInterval(() => {
            if (document.visibilityState !== 'visible') return;
            const next = currentScreen(pathRef.current);
            if (next && next !== screenRef.current) flush(false);
        }, SCREEN_WATCH_MS);

        function onVisibility() {
            if (document.visibilityState === 'hidden') flush(true);
            else lastTick.current = Date.now();
        }
        function onHide() {
            flush(true);
        }

        document.addEventListener('visibilitychange', onVisibility);
        window.addEventListener('pagehide', onHide);
        return () => {
            window.clearInterval(heartbeat);
            window.clearInterval(watch);
            document.removeEventListener('visibilitychange', onVisibility);
            window.removeEventListener('pagehide', onHide);
            flush(true);
        };
    }, []);

    useEffect(() => {
        pathRef.current = location.pathname;
        flushRef.current(false);
    }, [location.pathname]);

    return null;
}
